import React from "react";
import { Form, Field } from "formik";
import { useTranslation } from "react-i18next";
import ButtonLoadingSpinner from "./ButtonLoadingSpinner";
import AsyncSelect from "react-select/async";
import { useGetDefaultDeliveryOptionCitiesMutation } from "../store/services/smApi";
import { useLanguage } from "../store/slices/userSlice";

function AddressFormFields ({
  errors,
  touched,
  dirty,
  values,
  handleChange,
  handleBlur,
  isValid,
  setFieldValue,
  handleSubmit,
  status,
  editableAddress,
  isLoadingCreateUserAddressMutation,
  isLoadingUpdateUserAddressMutation,
  onCancel,
  options,
  forceCountry,
  withCityOptions
}) {
  const { t } = useTranslation();
  const language = useLanguage();
  const [getDefaultDeliveryOptionCities] = useGetDefaultDeliveryOptionCitiesMutation();

  const loadCityOptions = async (inputValue) => {
    try {
      const res = await getDefaultDeliveryOptionCities({ search: inputValue, page_size: 100 });
      if (res.data) {
        return res.data.results.map((city) => ({
          label: city[`${language}_name`],
          value: city.en_name
        }));
      }
      return [];
    } catch (error) {
      console.error("Error loading city options:", error);
      return [];
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {status && (
        Object.keys(status).map((key, n) => {
          if (!touched[key]) {
            return (
              <div key={n} className="error-msg error mt-4 text-danger">
                {t(key)}: {t(status[key][0])}
              </div>
            );
          }
          return null;
        })
      )}
      <div className="row align-items-center">
        <div className="col-12">
          <div className="form-group mt-3 mb-1 position-relative">
            <label htmlFor={"full_name"} className="form-label">
              {t("Full name")}
            </label>
            <div className={`position-relative ${
              errors.full_name && touched.full_name ? " is-invalid" : ""
            }`}>
              <Field
                type="text"
                className={`form-control ${
                  errors.full_name && touched.full_name ? " is-invalid" : ""
                }`}
                id={"full_name"}
                name="full_name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.full_name}
                autoFocus={!editableAddress}
              />
              {errors.full_name && touched.full_name
                ? (
                  <div className={"invalid-feedback start-0 top-100"}>
                    {t(errors.full_name)}
                  </div>
                  )
                : null}
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="form-group mt-3 mb-1 position-relative">
            <label htmlFor="phone" className="form-label">
              {t("Phone")}
            </label>
            <div className="position-relative">
              <Field
                type="text"
                className={`form-control ${
                  errors.phone && touched.phone ? " is-invalid" : ""
                }`}
                id="phone"
                name="phone"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.phone}
              />
              {errors.phone && touched.phone
                ? (
                  <div className={"invalid-feedback start-0 top-100"}>
                    {t(errors.phone)}
                  </div>
                  )
                : null}
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="form-group mt-3 mb-1">
            <label htmlFor="country" className="form-label">
              {t("Country")}
            </label>
            <div className="position-relative">
              <Field
                as="select"
                name="country"
                className={`form-select ${
                  errors.country && touched.country ? " is-invalid" : ""
                }`}
                onChange={handleChange}
                onBlur={handleBlur}
                value={forceCountry || values.country}
                defaultValue={forceCountry}
                disabled={forceCountry}
              >
                <option>{t("Select Country")}</option>
                {options.map((country) => (
                  <option key={country.label} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </Field>
              {errors.country && touched.country
                ? (
                  <div className="invalid-feedback start-0 top-100">
                    {t(errors.country)}
                  </div>
                  )
                : null}
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="form-group mt-3 mb-1 position-relative">
            <label htmlFor="street" className="form-label">
              {t("Street")}
            </label>
            <div className="position-relative">
              <Field
                type="text"
                className={`form-control ${
                  errors.street && touched.street ? " is-invalid" : ""
                }`}
                id="street-field"
                name="street"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.street}
              />
              {errors.street && touched.street
                ? (
                  <div className={"invalid-feedback start-0 top-100"}>
                    {t(errors.street)}
                  </div>
                  )
                : null}
            </div>
          </div>
        </div>
        <div className="col">
          <div className="form-group mt-3 mb-1">
            <label htmlFor="city" className="form-label">
              {t("City")}
            </label>
            <div className="position-relative">
              {/* <Field
                type="text"
                className={`form-control ${
                  errors.city && touched.city ? " is-invalid" : ""
                }`}
                id="city"
                name="city"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.city}
              /> */}
              {
                withCityOptions
                  ? (
                    <AsyncSelect
                      id="city"
                      name="city"
                      value={{ label: values.city, value: values.city }}
                      onChange={(option) => {
                        console.log("option", option);
                        setFieldValue("city", option?.label);
                      }}
                      onBlur={() => handleBlur({ target: { name: "city" } })}
                      isClearable
                      isSearchable
                      placeholder={t("Please search for a city")}
                      noOptionsMessage={(i) => t("No options try to search with another word")}
                      loadOptions={loadCityOptions}
                      classNamePrefix="react-select"
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          border: state.isFocused
                            ? "1px solid var(--bs-first)"
                            : errors.city && touched.city
                              ? "1px solid #dc3545"
                              : "1px solid #E4E9EF",
                          borderRadius: "8px",
                          boxShadow: "none",
                          maxHeight: "54px",
                          minHeight: "54px",
                          padding: "16px 20px",
                          backgroundColor: "transparent",
                          "&:hover": {
                            borderColor: state.isFocused
                              ? "var(--bs-first)"
                              : errors.city && touched.city
                                ? "#dc3545"
                                : "var(--bs-first)"
                          }
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: "var(--bs-secondary)",
                          fontSize: "1rem",
                          fontWeight: "500"
                        }),
                        input: (provided) => ({
                          ...provided,
                          color: "var(--bs-dark)",
                          fontSize: "1rem",
                          fontWeight: "500"
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: "var(--bs-dark)",
                          fontSize: "1rem",
                          fontWeight: "500"
                        }),
                        menu: (provided) => ({
                          ...provided,
                          border: "1px solid var(--bs-border)",
                          borderRadius: "8px",
                          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
                          zIndex: 9999,
                          backgroundColor: "white"
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? "var(--bs-first)"
                            : state.isFocused
                              ? "var(--bs-light-700)"
                              : "transparent",
                          color: state.isSelected ? "white" : "var(--bs-dark)",
                          fontSize: "1rem",
                          fontWeight: "500",
                          padding: "16px 20px",
                          "&:hover": {
                            backgroundColor: state.isSelected ? "var(--bs-first)" : "var(--bs-light-700)"
                          }
                        }),
                        clearIndicator: (provided) => ({
                          ...provided,
                          color: "var(--bs-secondary)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          "&:hover": {
                            color: "#dc3545"
                          },
                          marginTop: "-10px"
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          color: "var(--bs-secondary)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          "&:hover": {
                            color: "var(--bs-dark)"
                          },
                          marginTop: "-10px"
                        }),
                        indicatorSeparator: (provided) => ({
                          ...provided,
                          backgroundColor: "var(--bs-border)",
                          marginTop: "-5px"
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          padding: "0",
                          marginTop: "-10px"
                        })
                      }}
                    />
                    )
                  : (
                    <Field
                type="text"
                className={`form-control ${
                  errors.city && touched.city ? " is-invalid" : ""
                }`}
                id="city"
                name="city"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.city}
              />
                    )
              }
              {errors.city && touched.city
                ? (
                  <div className={"invalid-feedback start-0 top-100"}>
                    {t(errors.city)}
                  </div>
                  )
                : null}
            </div>
          </div>
        </div>
      </div>
      <div className="row">

        <div className="col-sm-6 col-xl-4">
          <div className="form-group mt-3 mb-1">
            <label htmlFor="state" className="form-label">
              {t("State")}
            </label>
            <div className="position-relative">
              <Field
                type="text"
                className={`form-control ${
                  errors.state && touched.state ? " is-invalid" : ""
                }`}
                id="state"
                name="state"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.state}
              />
              {errors.state && touched.state
                ? (
                  <div className={"invalid-feedback start-0 top-100"}>
                    {t(errors.state)}
                  </div>
                  )
                : null}
            </div>
          </div>
        </div>

        <div className="col-sm-6 col-xl-4">
          <div className="form-group mt-3 mb-1">
            <label htmlFor="building" className="form-label">
              {t("Apt, Suite, Other")}
            </label>
            <div className="position-relative">
              <Field
                type="text"
                className={`form-control ${
                  errors.building && touched.building ? " is-invalid" : ""
                }`}
                id="building"
                name="building"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.building}
              />
              {errors.building && touched.building
                ? (
                  <div className={"invalid-feedback start-0 top-100"}>
                    {t(errors.building)}
                  </div>
                  )
                : null}
            </div>
          </div>
        </div>

        <div className="col-sm-6 col-xl-4">
          <div className="form-group mt-3 mb-1">
            <label htmlFor="zip" className="form-label">
              {t("Zip Code")}
            </label>
            <div className="position-relative">
              <input
                type="text"
                className={`form-control ${
                  errors.zip && touched.zip ? " is-invalid" : ""
                }`}
                id="zip"
                name="zip"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.zip}
              />
              {errors.zip && touched.zip
                ? (
                  <div className={"invalid-feedback start-0 top-100"}>
                    {t(errors.zip)}
                  </div>
                  )
                : null}
            </div>
          </div>
        </div>
        <div className="col-12 mb-4">
          <div className="form-check mt-3">
            <input
              className="form-check-input"
              type="checkbox"
              name="is_default"
              id="new_address"
              checked={values.is_default}
              value={values.is_default}
              onChange={() => setFieldValue("is_default", !values.is_default)}
            />
            <label className="form-check-label text-muted" htmlFor="address1">
              {t("Make default")}
            </label>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className="form-group">
            <label className="form-label">&nbsp;</label>
            <button type="submit" className="btn btn-primary" disabled={!dirty}>
              {isLoadingCreateUserAddressMutation || isLoadingUpdateUserAddressMutation
                ? (
                  <ButtonLoadingSpinner />
                  )
                : (
                    t(`${editableAddress ? "Update" : "Create"}`)
                  )}
            </button>
            <button
              type="button"
              className="btn btn-secondary ms-2"
              onClick={onCancel}
            >
              {t("Cancel")}
            </button>
          </div>
        </div>
      </div>
    </Form>
  );
}

export default AddressFormFields;
